import { io, type Socket } from "socket.io-client";

import type { Cue } from "@/types";
import { SOCKET_URL } from "./config";
import { SOCKET_RECONNECT_OPTIONS } from "./socket-options";
import { READY_EVENT, socketRecovery } from "./socket-recovery";

export type CueSocketHandlers = {
  onCueReceived?: (cue: Cue) => void;
  /**
   * The connection was ADMITTED — first connection and every reconnect after
   * it (the server's `connection:ready`, never the pre-admission connect
   * ack). A cue that arrived while this was down was never delivered here, so
   * the inbox has to be re-read rather than waited on.
   */
  onConnect?: () => void;
};

export function createCueSocket(token: string, handlers: CueSocketHandlers) {
  const socket: Socket = io(`${SOCKET_URL}/cue`, {
    transports: ["websocket"],
    // Shared so sockets that CAN share a Manager do, and so a fleet-wide
    // reconnect spreads instead of arriving in one second. Sharing is not
    // guaranteed — recreating a namespace gets a new Manager (see
    // socket-options.ts) — which is why recovery is scoped per Manager.
    ...SOCKET_RECONNECT_OPTIONS,
    auth: { token },
  });

  // READY, not the bare connect ack: admission has not run at connect time,
  // and the inbox re-read this triggers must not fire at a refusing server.
  socket.on(READY_EVENT, () => {
    handlers.onConnect?.();
  });
  socket.on("cue:received", (cue: Cue) => handlers.onCueReceived?.(cue));

  // A server REFUSAL is the one disconnect socket.io never retries; the shared
  // coordinator brings this socket back (socket-recovery.ts).
  const unregister = socketRecovery.register(socket);

  return {
    disconnect: () => {
      unregister();
      socket.disconnect();
    },
  };
}
